import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Sparkles } from 'lucide-react-native';
import { memo, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BiButton } from '../../components/ui/BiButton';
import {
  BiPageHeader,
  kBiContentMaxWidth,
} from '../../components/ui/BiPageHeader';
import { BiRetry } from '../../components/ui/BiRetry';
import { useBiLibrary } from '../functions/useBiLibrary';
import { BuiltinTile } from './BuiltinTile';
import { DatasetTile } from './DatasetTile';
import { LibrarySection } from './LibrarySection';
import { UploadTile } from './UploadTile';

/**
 * The data a user can build on in this store: their uploads (with those
 * still in progress or failed), what colleagues shared, and the built-in
 * datasets.
 */
export const BiDatasetLibraryView = memo(
  ({
    mobile,
    onBack,
    onOpen,
    onAskAi,
  }: {
    mobile: boolean;
    onBack: () => void;
    /** An uploaded dataset id or a built-in model key. */
    onOpen: (datasetRef: string) => void;
    onAskAi: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const { state, refresh } = useBiLibrary();
    const [width, setWidth] = useState(0);
    return (
      <View style={{ flex: 1, backgroundColor: aiColors.background }}>
        <BiPageHeader
          title="我的数据"
          onBack={onBack}
          actions={
            <BiButton
              icon={Sparkles}
              title={mobile ? undefined : '上传并分析'}
              accessibilityLabel="上传并分析"
              tone="primary"
              onPress={onAskAi}
            />
          }
        />
        {state.kind !== 'ready' ? (
          <View
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            {state.kind === 'failed' ? (
              <BiRetry onRetry={refresh} />
            ) : (
              <ActivityIndicator color={aiColors.primary} />
            )}
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={{
              padding: rpx(16),
              paddingBottom: insets.bottom + rpx(32),
            }}
          >
            <View
              onLayout={event => setWidth(event.nativeEvent.layout.width)}
              style={{
                width: '100%',
                maxWidth: kBiContentMaxWidth,
                alignSelf: 'center',
                gap: rpx(24),
              }}
            >
              {width ? (
                <>
                  <LibrarySection
                    title="我上传的"
                    hint="新上传的数据只有你能看到，共享与否由你决定。"
                    empty="还没有上传过表格。在 AI 对话里上传 Excel 或 CSV，就会出现在这里。"
                    width={width}
                    tiles={[
                      ...state.library.uploads.map(upload => ({
                        key: upload.id,
                        node: (
                          <UploadTile upload={upload} onDeleted={refresh} />
                        ),
                      })),
                      ...state.library.mine.map(dataset => ({
                        key: dataset.id,
                        node: (
                          <DatasetTile
                            dataset={dataset}
                            onOpen={() => onOpen(dataset.id)}
                          />
                        ),
                      })),
                    ]}
                  />
                  <LibrarySection
                    title="共享给我"
                    empty="还没有同事共享数据给你。"
                    width={width}
                    tiles={state.library.shared.map(dataset => ({
                      key: dataset.id,
                      node: (
                        <DatasetTile
                          dataset={dataset}
                          onOpen={() => onOpen(dataset.id)}
                        />
                      ),
                    }))}
                  />
                  <LibrarySection
                    title="内置数据"
                    hint="门店系统里的业务数据，可以直接提问。"
                    empty="当前角色没有可用的内置数据。"
                    width={width}
                    tiles={state.library.builtin.map(({ model, catalog }) => ({
                      key: model,
                      node: (
                        <BuiltinTile
                          catalog={catalog}
                          onOpen={() => onOpen(model)}
                        />
                      ),
                    }))}
                  />
                </>
              ) : null}
            </View>
          </ScrollView>
        )}
      </View>
    );
  },
);
